import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Mic2, Sparkles, Gift, BadgeCheck, Wallet, Music2,
} from "lucide-react";
import Top10Leaderboard from "../components/Top10Leaderboard";
import AomHomeSection from "../components/AomHomeSection";
import TalentHomeSection from "../components/TalentHomeSection";
import CuratedSelections from "../components/CuratedSelections";
import FeedPreview from "../components/FeedPreview";
import SpotlightSection from "../components/SpotlightSection";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

// Image du hero hébergée avec le site (frontend/public/images), indépendante d'Emergent.
const HERO_BG = "/images/mboazick-hero-artiste.webp";
// Ancienne adresse chez Emergent : utilisée seulement si le fichier ci-dessus manque.
const HERO_BG_FALLBACK = "https://customer-assets.emergentagent.com/job_beat-vault-68/artifacts/2irigk7q_ChatGPT%20Image%2020%20juin%202026%2C%2016_51_44.png";
const PROFILE_IMG = "https://images.unsplash.com/photo-1520166012956-add9ba0835cb?auto=format&fit=crop&w=1200&q=80";
const MONEY_IMG = "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1200&q=80";
const AI_IMG = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1200&q=80";

export default function Landing() {
    const { user } = useAuth();
    // Main call to action depends on who is looking at the page.
    const cta = !user
        ? { to: "/register", label: "Créer mon profil gratuit" }
        : ["artist", "admin"].includes(user.role)
            ? { to: "/artist", label: "Aller à mon studio" }
            : { to: "/become-artist", label: "Devenir artiste" };
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
    }, []);

    return (
        <div className="bg-[#050505]" data-testid="landing-page">
            {/* ============ 1. HERO — un seul message, orienté bénéfice ============ */}
            <section className="relative overflow-hidden min-h-[88vh] flex items-center">
                <img decoding="async" src={HERO_BG} onError={(e) => { if (e.currentTarget.src !== HERO_BG_FALLBACK) e.currentTarget.src = HERO_BG_FALLBACK; }} alt="Artiste africain qui gère sa carrière musicale : presse, réseaux sociaux et concerts" className="absolute inset-0 w-full h-full object-cover opacity-35" fetchPriority="high" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
                    <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#ffc83d] border border-[#ffc83d]/30 bg-[#ffc83d]/10 rounded-full px-4 py-1.5 mb-6">
                        <Sparkles className="w-3.5 h-3.5" /> La plateforme pro des artistes africains
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-3xl">
                        <span className="text-[#00d97e]">Ta musique.</span>{" "}
                        <span className="text-[#ffc83d]">Tes fans.</span>
                        <br />
                        <span className="text-[#F4F4F4]">Tes revenus.</span>
                    </h1>
                    <p className="mt-6 text-[#C8C8C8] max-w-xl leading-relaxed text-base md:text-lg">
                        Crée ton profil pro, vends tes sons et reçois le soutien direct de tes fans —
                        <strong className="text-[#F4F4F4]"> payé par Mobile Money</strong>.
                    </p>
                    <div className="mt-9 flex flex-wrap items-center gap-4">
                        <Link to={cta.to} className="group inline-flex items-center gap-2 bg-[#00d97e] hover:bg-[#00f099] text-[#050505] font-semibold px-8 py-4 rounded-full transition-all hover:shadow-[0_0_40px_-5px_rgba(0,217,126,0.6)]" data-testid="cta-hero-register">
                            {cta.label} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                        <Link to="/producers" className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#F4F4F4] text-[#F4F4F4] font-medium px-7 py-4 rounded-full transition-colors" data-testid="cta-hero-directory">
                            Découvrir les artistes
                        </Link>
                    </div>
                    {!user && <p className="mt-4 text-xs text-[#6E6E6E]">Gratuit · 2 minutes · aucune carte bancaire requise</p>}
                </div>
            </section>

            {/* ============ 2. PREUVE SOCIALE ============ */}
            <section className="border-t border-[#1c1c1c] bg-[#0a0a0a]" data-testid="social-proof-bar">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-wrap items-center justify-center md:justify-between gap-6">
                    {stats?.latest_artists?.length > 0 && (
                        <div className="flex items-center gap-3">
                            <div className="flex -space-x-3">
                                {stats.latest_artists.slice(0, 5).map((a, i) => (
                                    <img decoding="async" key={i} src={a.avatar_url} alt={a.name} title={a.name}
                                        className="w-10 h-10 rounded-full object-cover ring-2 ring-[#0a0a0a]" loading="lazy" />
                                ))}
                            </div>
                            <p className="text-sm text-[#A8A8A8]">Ils sont déjà sur MBOAZICK</p>
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-8 md:gap-12">
                        {stats && stats.artists >= 10 ? (
                            <>
                                <Stat value={stats.artists} label="artistes" accent="#00d97e" />
                                <Stat value={stats.tracks} label="titres en ligne" accent="#ffc83d" />
                                <Stat value={(stats.plays || 0).toLocaleString("fr-FR")} label="écoutes" accent="#00c0ff" />
                            </>
                        ) : (
                            <p className="text-sm text-[#ffc83d] inline-flex items-center gap-2">
                                🎖️ Rejoins les premiers <strong>Artistes Fondateurs</strong> — avantages exclusifs au lancement
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* ============ 3. ARTISTE EN VEDETTE + SINGLE DE LA SEMAINE ============ */}
            <div className="mz-cv"><SpotlightSection /></div>

            {/* ============ 4. Playlists curatées : À la une + Sélection de l'équipe ============ */}
            <div className="mz-cv"><CuratedSelections /></div>

            {/* ============ 5. CE QUI BOUGE — extrait du fil d'actu ============ */}
            <div className="mz-cv"><FeedPreview /></div>

            {/* ============ 6. TOP 10 MBOA — classé du plus écouté ============ */}
            <div className="mz-cv"><Top10Leaderboard /></div>

            {/* ============ 6b. ARTISTE DU MOIS — concept, sponsor, top 5 ============ */}
            <div className="mz-cv"><AomHomeSection /></div>

            {/* ============ 6c. MBOAZICK TALENT — concept + édition en cours ============ */}
            <div className="mz-cv"><TalentHomeSection /></div>

            {/* ============ 7. LES 3 BÉNÉFICES CLÉS ============ */}
            <section className="border-t border-[#1c1c1c]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#00d97e] mb-3">Pourquoi MBOAZICK</p>
                        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight">Tout ce qu&apos;il te faut pour vivre de ta musique.</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <BenefitCard
                            img={PROFILE_IMG}
                            icon={<BadgeCheck className="w-5 h-5" />}
                            accent="#00d97e"
                            title="Ta vitrine professionnelle"
                            text="Une page publique à ton nom (mboazick.com/artistes/toi) : bio, photos, clips, discographie et bouton Book Me. Les organisateurs te trouvent et te bookent directement."
                            testid="benefit-profile"
                        />
                        <BenefitCard
                            img={MONEY_IMG}
                            icon={<Wallet className="w-5 h-5" />}
                            accent="#ffc83d"
                            title="Tes fans te financent"
                            text="Fixe le prix de tes sons (tu touches 70 %, 85 % en Premium), lance tes préventes et reçois des cadeaux 🌹❤️🍻💎 de tes fans. Tes gains sont versés par Mobile Money dès 30 000 FCFA."
                            testid="benefit-money"
                        />
                        <BenefitCard
                            img={AI_IMG}
                            icon={<Mic2 className="w-5 h-5" />}
                            accent="#ff6b35"
                            title="Les spectacles viennent à toi"
                            text="Festivals, concours, castings : découvre les opportunités où candidater et reçois des demandes de booking directement sur ton profil. Ta carrière décolle."
                            testid="benefit-bookings"
                        />
                    </div>
                    <div className="mt-12 text-center">
                        <Link to={cta.to} className="group inline-flex items-center gap-2 bg-[#ffc83d] hover:bg-[#ffd866] text-[#050505] font-semibold px-8 py-3.5 rounded-full transition-all hover:shadow-[0_0_40px_-5px_rgba(255,200,61,0.5)]" data-testid="cta-benefits-register">
                            {user ? cta.label : "Je crée mon profil"} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ 8. COMMENT ÇA MARCHE — 3 étapes ============ */}
            <section className="border-t border-[#1c1c1c]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#ffc83d] mb-3">Comment ça marche</p>
                        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight">En ligne en 3 étapes.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { n: "01", icon: <Mic2 className="w-5 h-5" />, t: "Inscris-toi gratuitement", d: "Choisis ton rôle (artiste, fan, prestataire, organisateur, média). 2 minutes, aucune carte requise." },
                            { n: "02", icon: <Music2 className="w-5 h-5" />, t: "Publie ton profil et tes sons", d: "L'IA rédige ta bio, tu ajoutes tes titres et clips. Ton profil pro est en ligne avec ton lien personnalisé." },
                            { n: "03", icon: <Gift className="w-5 h-5" />, t: "Encaisse par Mobile Money", d: "Ventes de sons et cadeaux de fans s'accumulent sur ton solde, versé sur ton Mobile Money. Les demandes de booking arrivent dans ta boîte." },
                        ].map((s) => (
                            <div key={s.n} className="bg-[#0d0d0d] border border-[#1c1c1c] rounded-2xl p-8 hover:border-[#00d97e]/40 hover:-translate-y-1 transition-all" data-testid={`step-${s.n}`}>
                                <div className="flex items-center justify-between">
                                    <p className="font-display text-5xl text-[#ffc83d]/80 font-semibold">{s.n}</p>
                                    <div className="w-11 h-11 rounded-full bg-[#00d97e]/10 border border-[#00d97e]/40 text-[#00d97e] flex items-center justify-center">{s.icon}</div>
                                </div>
                                <p className="mt-6 text-lg font-medium">{s.t}</p>
                                <p className="mt-2 text-sm text-[#A8A8A8] leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ 6. CTA FINAL ============ */}
            <section className="relative border-t border-[#1c1c1c] overflow-hidden">
                <div className="pointer-events-none absolute -top-32 -left-40 w-[560px] h-[560px] rounded-full bg-[#00d97e]/10 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-32 -right-40 w-[560px] h-[560px] rounded-full bg-[#ffc83d]/10 blur-3xl" />
                <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
                    <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                        Ta musique a de la valeur.
                        <br />
                        <span className="text-gradient-tribal">Il est temps qu&apos;elle te rapporte.</span>
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to={cta.to} className="group inline-flex items-center gap-2 bg-[#00d97e] hover:bg-[#00f099] text-[#050505] font-semibold px-9 py-4 rounded-full text-lg transition-all hover:shadow-[0_0_50px_-5px_rgba(0,217,126,0.6)]" data-testid="cta-final-register">
                            {cta.label} <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                    <p className="mt-4 text-xs text-[#6E6E6E]">Artistes · Fans · Prestataires · Organisateurs · Médias — chacun a sa place.</p>
                </div>
            </section>
        </div>
    );
}

function Stat({ value, label, accent }) {
    return (
        <div className="text-center">
            <p className="font-display text-2xl md:text-3xl font-semibold" style={{ color: accent }}>{value}</p>
            <p className="text-[11px] uppercase tracking-widest text-[#6E6E6E]">{label}</p>
        </div>
    );
}

function BenefitCard({ img, icon, accent, title, text, testid }) {
    return (
        <div className="relative overflow-hidden rounded-2xl border border-[#1c1c1c] group hover:border-[var(--c)] transition-colors min-h-[380px] flex" style={{ "--c": `${accent}80` }} data-testid={testid}>
            <img decoding="async" src={img} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-35 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
            <div className="relative p-8 flex flex-col justify-end">
                <div className="w-11 h-11 rounded-full flex items-center justify-center border" style={{ borderColor: `${accent}66`, background: `${accent}1A`, color: accent }}>
                    {icon}
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-[#C8C8C8] leading-relaxed">{text}</p>
            </div>
        </div>
    );
}
